/**
 * @fileoverview Reset-password mutation hook.
 * @description Wraps POST /identity/password/reset in a TanStack mutation. The backend issues no
 * session on success (every session is revoked and a fresh Login + MFA is required), so on success
 * this sends the visitor to /login with the same one-shot "password changed, sign in again"
 * notice useChangePassword uses. Once the link is spent (success, or a 1031 saying it no longer
 * works) the cached verification is dropped, so reopening the link re-verifies it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router'

import { VERIFY_PASSWORD_RESET_QUERY_KEY } from '@/features/auth/hooks/useVerifyPasswordReset'
import {
  resetPassword,
  type ResetPasswordRequest,
  type ResetPasswordResponse,
} from '@/features/auth/services/password-reset.service'
import { markPasswordChangedNotice } from '@/features/auth/utils/password-changed-notice.utils'
import { ERROR_CODES } from '@/shared/constants/error-codes.constants'
import { ROUTES } from '@/shared/constants/routes.constants'

import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Resets the password with a reset-link token, then sends the visitor to /login.
 * @returns {ReturnType<typeof useMutation<ResetPasswordResponse, ApiError, ResetPasswordRequest>>}
 * The mutation object (mutate, isPending, error, ...).
 */
export function useResetPassword() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  return useMutation<ResetPasswordResponse, ApiError, ResetPasswordRequest>({
    mutationFn: resetPassword,
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: VERIFY_PASSWORD_RESET_QUERY_KEY })
      markPasswordChangedNotice()
      void navigate(ROUTES.LOGIN, { replace: true })
    },
    onError: (error) => {
      if (error.code === ERROR_CODES.PASSWORD_RESET_TOKEN_INVALID) {
        queryClient.removeQueries({ queryKey: VERIFY_PASSWORD_RESET_QUERY_KEY })
      }
    },
  })
}
